import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Alert,
  Tag,
  Table,
  Button,
  Space,
  Divider,
  message,
} from "antd";
import {
  ShoppingOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import dayjs from "dayjs";
import UnifiedCreditDisplay from "../../components/UnifiedCreditDisplay"; // New component
import {
  calculateAvailableCreditLimit,
  calculateCreditUtilization,
  sumOrdersTotal,
} from "../../utils/creditUtils";

const { Title } = Typography;

// Currency format utility
const formatCurrency = (amount, currency = "USD") => {
  if (!amount && amount !== 0) return "0.00";
  const num = Number(amount);
  if (isNaN(num)) return "0.00";

  if (currency === "HKD") return `HK$${num.toFixed(2)}`;
  if (currency === "MYR") return `MYR ${num.toFixed(2)}`;
  return `$${num.toFixed(2)}`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalSpent: 0,
    recentOrders: [],
    recentVouchers: [],

    // Account system data fields
    topUpBalance: 0, // Prepaid credit
    creditLimit: 0, // Credit limit
    creditUsed: 0, // Credit used
    availableCredit: 0, // Available credit
    creditUtilization: 0, // Credit utilization
    currency: "USD",

    // Account status
    canOrder: true,

    // Detailed breakdown
    breakdown: null,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch comprehensive dashboard data using new APIs.
      // Counts use limit=1 + pagination.totalCount so the dashboard reflects
      // *all* orders, not just the first page used to render "recent orders".
      const [ordersRes, balanceRes, vouchersRes, pendingRes, completedRes] =
        await Promise.all([
          axios.get("/orders?limit=10&sortBy=createdAt&sortOrder=desc"),
          axios.get("/client/balance"),
          axios.get("/vouchers?limit=5&sortBy=createdAt&sortOrder=desc"),
          axios.get("/orders?limit=1&status=pending,confirmed,processing"),
          axios.get(
            "/orders?limit=1&status=completed,shipped,delivered",
          ),
        ]);

      const orders = ordersRes.data.orders || [];
      const balanceData = balanceRes.data;
      const vouchers = (vouchersRes.data.data?.vouchers || []).filter(
        (v) => v.isActive !== false && v.voucherStatus === "online",
      );

      const totalOrders = ordersRes.data.pagination?.totalCount || 0;
      const pendingOrders =
        pendingRes.data.pagination?.totalCount ??
        orders.filter((order) =>
          ["pending", "confirmed", "processing"].includes(order.status),
        ).length;
      // The backend has migrated to a simplified status set (`completed`)
      // but legacy orders may still carry `shipped` / `delivered`. Treat
      // all three as "completed" so the counter doesn't silently drop them.
      const completedOrders =
        completedRes.data.pagination?.totalCount ??
        orders.filter((order) =>
          ["completed", "shipped", "delivered"].includes(order.status),
        ).length;
      const totalSpent = sumOrdersTotal(orders);

      const backendCreditUsed = balanceData.creditUsed || 0;

      setDashboardData({
        totalOrders,
        pendingOrders,
        completedOrders,
        totalSpent,
        recentOrders: orders.slice(0, 5),
        recentVouchers: vouchers,

        topUpBalance: balanceData.topUpBalance || 0,
        creditLimit: balanceData.creditLimit || 0,
        creditUsed: backendCreditUsed,
        availableCredit: calculateAvailableCreditLimit({
          topUpBalance: balanceData.topUpBalance,
          creditLimit: balanceData.creditLimit,
          creditUsed: backendCreditUsed,
        }),
        creditUtilization: calculateCreditUtilization({
          topUpBalance: balanceData.topUpBalance,
          creditLimit: balanceData.creditLimit,
          creditUsed: backendCreditUsed,
        }),
        currency: balanceData.currency || "USD",

        // Account status
        canOrder: balanceData.canOrder !== false,

        // Detailed breakdown
        breakdown: balanceData.breakdown || null,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      message.error(
        error?.response?.data?.message || "Failed to load dashboard data"
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      // Actual backend order statuses
      draft: "default",
      pending: "orange",
      confirmed: "blue",
      processing: "purple",
      shipped: "green",
      delivered: "green",
      insufficient_balance: "red",
      out_of_stock: "red",
      cancelled: "default",
      refunded: "magenta",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      // Actual backend order statuses
      draft: "Draft",
      pending: "Pending",
      confirmed: "Confirmed",
      processing: "Processing",
      shipped: "Shipped",
      delivered: "Delivered",
      insufficient_balance: "Insufficient Balance",
      out_of_stock: "Out of Stock",
      cancelled: "Cancelled",
      refunded: "Refunded",
    };
    return labels[status] || status;
  };

  const getCreditAlert = () => {
    const { creditUsed, creditLimit, currency } = dashboardData;
    const availableCredit = Math.max(0, creditLimit - creditUsed);
    const utilization = creditLimit > 0 ? (creditUsed / creditLimit) * 100 : 0;

    if (utilization >= 90) {
      return {
        type: "error",
        message:
          "Credit limit almost exhausted! Please add more credit to continue ordering.",
        icon: <ExclamationCircleOutlined />,
      };
    } else if (utilization >= 75) {
      return {
        type: "warning",
        message:
          "High credit utilization. Consider adding more credit or making a payment.",
        icon: <ClockCircleOutlined />,
      };
    } else if (availableCredit > 0) {
      return {
        type: "success",
        message: `You have ${formatCurrency(
          availableCredit,
          currency
        )} available credit.`,
        icon: <CheckCircleOutlined />,
      };
    }
    return null;
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2} className="page-title">
          Welcome back, {user?.firstName}!
        </Title>
        <Typography.Text type="secondary">
          {user?.client?.companyName}
        </Typography.Text>
      </div>

      {/* Account Status Alert - Consolidated */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          {!dashboardData.canOrder && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              message="Account Ordering Restricted"
              description="Your account is currently restricted. Please contact support or top up your account."
              showIcon
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={() => navigate("/client/account/repayment")}
                >
                  Repay Credit
                </Button>
              }
            />
          )}

          {getCreditAlert() && dashboardData.canOrder && (
            <Alert
              type={getCreditAlert().type}
              message={getCreditAlert().message}
              icon={getCreditAlert().icon}
              showIcon
              closable
            />
          )}
        </Col>
      </Row>

      {/* Unified credit display */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <UnifiedCreditDisplay
            accountData={{
              topUpBalance: dashboardData.topUpBalance,
              creditLimit: dashboardData.creditLimit,
              creditUsed: dashboardData.creditUsed,
              currency: dashboardData.currency,
            }}
            loading={loading}
            showActions={true}
          />
        </Col>
      </Row>

      {/* Merged order overview cards */}
      <Card
        style={{ marginBottom: 24 }}
        title={<span>📋 Order Overview</span>}
        loading={loading}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<ShoppingOutlined />}
              onClick={() => navigate("/client/vouchers")}
            >
              Browse Vouchers
            </Button>
            {dashboardData.recentOrders.length > 0 && (
              <Button
                icon={<FileTextOutlined />}
                onClick={() =>
                  navigate(
                    `/client/orders/${dashboardData.recentOrders[0]._id}`
                  )
                }
              >
                View Latest Order
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={32} style={{ marginBottom: 16 }}>
          <Col>
            <Statistic
              title="Total Orders"
              value={dashboardData.totalOrders}
              prefix={<FileTextOutlined />}
              valueStyle={{ fontSize: "18px" }}
            />
          </Col>
          <Col>
            <Statistic
              title="Total Spent"
              value={dashboardData.totalSpent}
              prefix={(() => {
                const currency = dashboardData.currency || "USD";
                switch (currency) {
                  case "MYR":
                    return "RM";
                  case "CNY":
                    return "¥";
                  case "HKD":
                    return "HK$";
                  case "SGD":
                    return "S$";
                  case "USD":
                  default:
                    return "$";
                }
              })()}
              precision={2}
              valueStyle={{ fontSize: "18px", color: "#722ed1" }}
              suffix={dashboardData.currency}
            />
          </Col>
        </Row>

        {/* Recent Orders Table */}
        {dashboardData.recentOrders.length > 0 && (
          <>
            <Divider orientation="left">Recent Orders</Divider>
            <Table
              dataSource={dashboardData.recentOrders}
              pagination={false}
              size="small"
              rowKey="_id"
              columns={[
                {
                  title: "Order #",
                  dataIndex: "orderNumber",
                  key: "orderNumber",
                  render: (orderNumber, record) => (
                    <Button
                      type="link"
                      onClick={() => navigate(`/client/orders/${record._id}`)}
                      style={{ padding: 0 }}
                    >
                      {orderNumber || `ORD-${record._id?.slice(-8)}`}
                    </Button>
                  ),
                },
                {
                  title: "Date",
                  dataIndex: "createdAt",
                  key: "createdAt",
                  render: (date) => dayjs(date).format("MMM DD, YYYY"),
                },
                {
                  title: "Amount",
                  dataIndex: "totalAmount",
                  key: "totalAmount",
                  render: (amount) =>
                    formatCurrency(amount, dashboardData.currency),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => (
                    <Tag color={getStatusColor(status)}>
                      {getStatusLabel(status)}
                    </Tag>
                  ),
                },
              ]}
            />
          </>
        )}
      </Card>

      {/* Quick Actions Card */}
      <Card title="🚀 Quick Actions" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Button
              block
              size="large"
              icon={<ShoppingOutlined />}
              onClick={() => navigate("/client/vouchers")}
            >
              Browse Vouchers
            </Button>
          </Col>
          <Col xs={24} sm={6}>
            <Button
              block
              size="large"
              icon={<FileTextOutlined />}
              onClick={() => navigate("/client/orders")}
            >
              Order History
            </Button>
          </Col>
          <Col xs={24} sm={6}>
            <Button
              block
              size="large"
              icon={<CreditCardOutlined />}
              onClick={() => navigate("/client/account/transactions")}
            >
              Transaction History
            </Button>
          </Col>
        </Row>
      </Card>

    </div>
  );
};

export default Dashboard;
